import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faRightLeft, faTrash, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import ClickAwayListener from 'react-click-away-listener';
import { EmailTemplate } from '@spike/email-marketing-model';
import { fetchEmailTemplatesThunk } from '@spike/email-marketing-action';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner';
import EmailTemplatePreviewModal from 'components/EmailMarketing/EmailTemplates/Gallery/EmailTemplatePreviewModal';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import styles from './EmailTemplateContent.module.scss';

interface Props {
    templateId: number | null;
    onSelect: () => void;
    onRemove: () => void;
}

/**
 * The Content block of an email step: pick a template, then show it with a
 * thumbnail and a menu to preview, swap or clear it.
 *
 * The step stores only the id — the template list already lives in Redux for
 * the email marketing screens, so the row is resolved from there rather than
 * duplicating the HTML into the automation.
 */
const EmailTemplateContent: FunctionComponent<Props> = ({ templateId, onSelect, onRemove }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const templates = useSelector((state: RootState) => state.emailTemplates.list);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const template = templates?.find((t: EmailTemplate) => t.id === templateId) ?? null;

    // Reopening a saved automation lands here with an id but nothing in the
    // store to resolve it against. Scope 'own' matches what the picker fetches;
    // the default scope returns the system seeds, which never contain it.
    const requested = useRef(false);

    useEffect(() => {
        if (templateId === null || template || requested.current) return;
        requested.current = true;

        (dispatch as any)(fetchEmailTemplatesThunk(apiClientWrapper, 'own'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, template]);

    if (templateId === null) {
        return (
            <div className={styles.emptyRow}>
                <div className={styles.sectionLabel}>Content</div>
                <Button
                    label="Select Template"
                    variant="outlined"
                    size="small"
                    onClick={onSelect}
                />
            </div>
        );
    }

    return (
        <>
            <div className={styles.header}>
                <div className={styles.sectionLabel}>Content</div>
                <div className={styles.headerActions}>
                    <Button
                        label="Preview"
                        variant="outlined"
                        size="small"
                        onClick={() => setShowPreview(true)}
                    />
                    <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                        <div className={styles.menuWrapper}>
                            <button
                                type="button"
                                className={styles.menuBtn}
                                aria-label="Template options"
                                onClick={() => setMenuOpen(o => !o)}
                            >
                                <FontAwesomeIcon icon={faEllipsisVertical} />
                            </button>
                            {menuOpen && (
                                <div className={styles.menu}>
                                    <button
                                        type="button"
                                        className={styles.menuItem}
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onSelect();
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faRightLeft} />
                                        Change Template
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.menuItemDanger}
                                        onClick={() => {
                                            setMenuOpen(false);
                                            onRemove();
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                        Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    </ClickAwayListener>
                </div>
            </div>

            {template ? (
                <div className={styles.thumbnail}>
                    {/* sandboxed + inert: template HTML is user-authored and must
                        not run scripts or steal clicks inside the sidebar. */}
                    <iframe
                        title={template.name}
                        className={styles.thumbnailFrame}
                        sandbox=""
                        srcDoc={template.html}
                    />
                    <span className={styles.thumbnailName}>{template.name}</span>
                </div>
            ) : (
                <div className={styles.thumbnailLoading}>
                    <Spinner />
                </div>
            )}

            <div className={styles.notice}>
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>Dynamic content may not appear completely in previews</span>
            </div>

            {showPreview && (
                <EmailTemplatePreviewModal
                    template={template}
                    onClose={() => setShowPreview(false)}
                />
            )}
        </>
    );
};

export default EmailTemplateContent;
